<template>
  <article class="news-card-wrapper group">
    <!-- Image Container -->
    <div class="news-image-container">
      <img class="news-image" :src="getImage" :alt="item.title" />
      <div class="image-overlay"></div>
      
      <!-- Entity Badge -->
      <div v-if="getEntityImage" class="entity-badge">
        <img class="entity-logo" :src="getEntityImage" :alt="item.entity || 'Entity'" />
      </div>

      <!-- Read More Overlay -->
      <router-link :to="getNewsLink" class="read-more-overlay">
        <span class="read-more-text">Read More</span>
        <svg class="read-more-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
        </svg>
      </router-link>
    </div>

    <!-- Content -->
    <div class="news-card-content">
      <h3 class="news-title">{{ item.title }}</h3>
      
      <p v-if="item.description" class="news-description">
        {{ item.description }}
      </p>

      <!-- Footer -->
      <div class="news-footer">
        <div class="news-date">
          <svg class="date-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
          <span>{{ newsDate }}</span>
        </div>
        <router-link :to="getNewsLink" class="read-more-link">
          Read More
          <svg class="link-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
          </svg>
        </router-link>
      </div>
    </div>
  </article>
</template>

<script>
import FCHSLogo from '@/assets/images/dashboard/alumni/FCHS.png';
import ADPOLYLogo from '@/assets/images/dashboard/alumni/ADPOLY.png';
import ADVETILogo from '@/assets/images/dashboard/alumni/ADVETI.png';
import ATSLogo from '@/assets/images/dashboard/alumni/ATS.png';

export default {
  name: "news-card",
  props: ["item"],
  computed: {
    getImage() {
      if (!this.item.thumbnail) return '';
      if (typeof this.item.thumbnail === 'string' && (this.item.thumbnail.indexOf('http://') === 0 || this.item.thumbnail.indexOf('https://') === 0)) {
        return this.item.thumbnail;
      }
      return process.env.VUE_APP_PUBLIC_URL + this.item.thumbnail;
    },
    getEntityImage() {
      if (!this.item.entity) return '';

      // If entity is an object (legacy support), use logo
      if (typeof this.item.entity === 'object' && this.item.entity.logo) {
        return process.env.VUE_APP_PUBLIC_URL + this.item.entity.logo;
      }

      // Entity is a string, use logo mapping
      const entityStr = typeof this.item.entity === 'string' ? this.item.entity : '';
      const logos = {
        FCHS: FCHSLogo,
        ADPOLY: ADPOLYLogo,
        ADVETI: ADVETILogo,
        ATS: ATSLogo,
        ATHS: ATSLogo,
      };
      return logos[entityStr] || '';
    },
    newsDate() {
      const parsedDate = new Date(this.item.news_date);
      return parsedDate.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).replace(/\//g, '-');
    },
    getNewsLink() {
      // Use slug if available, otherwise fall back to id
      if (this.item.slug) {
        return `/news/${this.item.slug}`;
      }
      return `/news/${this.item.id}`;
    },
  },
}
</script>

<style lang="scss" scoped>
.news-card-wrapper {
  background: white;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px -4px rgba(0, 0, 0, 0.15);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #394867 0%, #499DD4 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }
}

.news-image-container {
  position: relative;
  width: 100%;
  height: 220px;
  overflow: hidden;
  background: linear-gradient(135deg, #e2e8f0, #cbd5e1);
}

.news-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.news-card-wrapper:hover .news-image {
  transform: scale(1.1);
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 100%);
  opacity: 0;
  transition: opacity 0.4s ease;
  pointer-events: none;
}

.news-card-wrapper:hover .image-overlay {
  opacity: 1;
}

.entity-badge {
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 0.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
  transition: transform 0.3s ease;
}

.news-card-wrapper:hover .entity-badge {
  transform: scale(1.05);
}

.entity-logo {
  height: 2rem;
  width: auto;
  display: block;
}

.read-more-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
  background: linear-gradient(to top, rgba(57, 72, 103, 0.95) 0%, rgba(73, 157, 212, 0.85) 100%);
  color: white;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.875rem;
  transform: translateY(100%);
  transition: transform 0.3s ease;
  z-index: 10;
}

.news-card-wrapper:hover .read-more-overlay {
  transform: translateY(0);
}

.read-more-icon {
  width: 1rem;
  height: 1rem;
  transition: transform 0.3s ease;
}

.news-card-wrapper:hover .read-more-icon {
  transform: translateX(4px);
}

.news-card-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
}

.news-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.75rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color 0.3s ease;
}

.news-card-wrapper:hover .news-title {
  color: #499DD4;
}

.news-description {
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.6;
  flex: 1;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.news-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
  margin-top: auto;
}

.news-date {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
}

.date-icon {
  width: 0.875rem;
  height: 0.875rem;
  color: #499DD4;
}

.read-more-link {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #394867;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    color: #499DD4;
    gap: 0.5rem;
  }

  .link-icon {
    width: 1rem;
    height: 1rem;
    transition: transform 0.3s ease;
  }

  &:hover .link-icon {
    transform: translateX(4px);
  }
}
</style>